/*
 * src/utils/auth.ts
 * Description: Utility functions for handling authentication, including login, logout, and retrieving user roles.
 */
import { gql, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { store, persistor } from '../store';
import { setUserAuthenticated, setToken, setUserData, setIsAdmin, clearSessionData } from '../store/slices/userSlice';
import { resetInvitationState } from '../store/slices/invitationSlice';
import { resetBitmapState } from '../store/slices/bitmapSlice';
import { resetDesignState } from '../store/slices/designSlice';
import { resetContactsState } from '../store/slices/contactSlice';
import { navigate } from 'gatsby';
import { createApolloClient } from '../wrap-with-provider';

/*

export const registerNewUser = async (username: string, email: string, password: string): Promise<void> => {
    try {
        const response = await fetch('http://invidoo.dk/wp-json/wp/v2/users/register', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, email, password }),
        });

        if (response.ok) {
            // Send confirmation email with token
            const token = generateToken(email);  // Custom token generation logic
            await sendConfirmationEmail(email, token);  // Email utility
        } else {
            console.error('User registration failed.');
        }
    } catch (error) {
        console.error('Error during registration:', error);
    }
};

*/
/*
export const registerUser = async (username: string, email: string, password: string): Promise<void> => {
    try {
        const response = await fetch(`${process.env.GATSBY_BACKEND_URL}/wp-json/wp/v2/users/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, email, password }),
        });

        if (response.ok) {
            // Send confirmation email with token
            const token = generateToken(email);  // Custom token generation logic
            await sendConfirmationEmail(email, token);  // Email utility
        } else {
            console.error('User registration failed.');
        }
    } catch (error) {
        console.error('Error during registration:', error);
    }
};*/

// --- ADD LoginResponseData INTERFACE --- 
// Adjust this interface based on the actual structure of the JSON response from your token endpoint

export interface LoginResponseData {
  token: string;
  user_email: string;
  user_nicename: string;
  user_display_name: string;
  // IMPORTANT: Add other fields that your backend login response provides,
  // especially if it includes a nested 'user' object with id, roles, custom fields, etc.
  // For example, if your backend directly returns WordPress user fields like in WP default /users/me:
  user?: { // This is an EXAMPLE structure, adjust to your backend's actual response
    id: number | string; // WP user ID is usually number, but API might send as string
    name?: string; // display_name
    first_name?: string;
    last_name?: string;
    slug?: string; // user_nicename
    description?: string;
    roles?: string[];
    // Add any other fields you might get like phone, address, ACF fields for user, etc.
    // Example for custom fields (you'll need to know their exact names):
    // company?: string;
    // phone_number?: string;
    favorite_design_ids?: number[];
    user_registered?: string; // WP default for registration date
    phone?: string; // Assuming you might have this
  };
  // Add any other top-level fields from the login response if needed
  // E.g., expires_in?: number;
}

// Interface for a potential error response from the token endpoint
interface LoginErrorResponseData {
  code?: string;
  message?: string;
  data?: { status?: number };
}

export const loginUser = async (username: string, password: string): Promise<LoginResponseData | null> => {
  const backendUrl = process.env.GATSBY_BACKEND_URL || '';
  try {
    const response = await fetch(`${backendUrl}/wp-json/jwt-auth/v1/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    // Parse JSON first, then check response.ok
    const responseBody: LoginResponseData | LoginErrorResponseData = await response.json();
    
    if (response.ok && (responseBody as LoginResponseData).token) {
      // It's a successful response, cast and return
      return responseBody as LoginResponseData;
    } else {
      // It's an error or unexpected response structure
      const serverMessage = (responseBody as LoginErrorResponseData).message || '';
      const errorCode = (responseBody as LoginErrorResponseData).code || '';
   
      // Check for email not verified error
      if (errorCode === 'email_not_verified') {
        throw new Error('Du skal bekræfte din email før du kan logge ind.<br/>Tjek din indbakke for bekræftelseslinket. Hvis du ikke kan finde emailen, tjek din spam-mappe eller send en besked til vores <a href="/support/">support</a>.');
      }
   
      if (serverMessage.includes('Unknown email') || 
          serverMessage.includes('Unknown username') ||
          serverMessage.includes('Ukendt mail') ||
          serverMessage.includes('Ukendt mailadresse') ||
          serverMessage.includes('Ukendt brugernavn')) {
        throw new Error('Der findes ingen konto med denne e-mail.<br/>Opret en gratis konto via linket herunder.');
      }

      if (serverMessage.includes('Mistet din adgangskode?') || 
          serverMessage.includes('incorrect_password') ||
          serverMessage.includes('The password you entered') ||
          errorCode === 'jwt_auth_failed' ||
          (!response.ok && response.status === 403)) {
        
        throw new Error('Adgangskoden er ikke korrekt. <br/>Prøv igen, eller vælg "Glemt adgangskode?" for at nulstille den.');
      }
      
      // Check for specific error patterns that indicate unactivated account
      if (serverMessage.includes('Der er sket en kritisk fejl') || 
          serverMessage.includes('critical error') ||
          serverMessage.includes('Wordpress') ||
          serverMessage.includes('fejlsøgning') ||
          errorCode === 'user_not_activated' ||
          (!response.ok && response.status === 403)) {
        
        throw new Error('Du har endnu ikke aktiveret din konto. Tjek din e-mail og klik på aktiveringslinket for at færdiggøre din registrering. Hvis du ikke kan finde e-mailen, tjek din spam-mappe eller send en besked til vores <a href="/support/">support</a>.');
      }

      // For other errors, show the server message or a generic fallback
      const fallbackMessage = serverMessage || 'Login fejlede. Tjek dine loginoplysninger og prøv igen.';
      throw new Error(fallbackMessage);
    }
  } catch (error) {
    // If error is already an Error instance, rethrow it, otherwise wrap it
    if (error instanceof Error) {
        console.error('Login failed - fetch/network or parsing error', { error: error.message });
    throw error;
    }
    console.error('Login failed - unknown error type', { error });
    throw new Error('An unexpected error occurred during login.');
  }
};

/**
 * Comprehensive reset function that clears ALL possible state
 * @param options Configuration options for what to reset
 */
export interface ResetOptions {
  clearReduxState?: boolean;      // Reset all Redux slices (default: true)
  clearPersistentState?: boolean;  // Purge redux-persist (default: true)
  clearApolloCache?: boolean;      // Clear Apollo Client cache (default: true)
  clearLocalStorage?: boolean;     // Clear specific localStorage items (default: true)
  clearSessionStorage?: boolean;   // Clear sessionStorage (default: true)
  clearBrowserStorage?: boolean;   // Clear all browser storage (default: false)
  keepUserPreferences?: boolean;   // Keep some user preferences (default: false)
  redirectTo?: string;            // Path to navigate after logout (default: '/log-ind/')
}

export const comprehensiveReset = async (options: ResetOptions = {}): Promise<void> => {
  const {
    clearReduxState = true,
    clearPersistentState = true,
    clearApolloCache = true,
    clearLocalStorage = true,
    clearSessionStorage = true,
    clearBrowserStorage = false,
    keepUserPreferences = false
  } = options;

  try {
    // 1. Clear Redux state
    if (clearReduxState) {
      // Reset user state
      store.dispatch(setUserAuthenticated(false));
      store.dispatch(setUserData(null));
      store.dispatch(setIsAdmin(false));
      store.dispatch(setToken(null));
      store.dispatch(clearSessionData());
      
      // Reset all other slices
      store.dispatch(resetInvitationState());
      store.dispatch(resetDesignState());
      store.dispatch(resetBitmapState());
      store.dispatch(resetContactsState());
    }

    // 2. Clear Apollo Client cache
    if (clearApolloCache) {
      try {
        const token = null; // No token needed during reset
        const client = createApolloClient(token);

        if (client) {
          await client.clearStore();
          // Also reset the cache completely
          await client.resetStore();
          
          // Force a small delay to ensure the cache clearing is complete
          await new Promise(resolve => setTimeout(resolve, 50));
        } else {
          console.warn('Apollo Client is null, skipping cache clear');
        }
      } catch (error) {
        console.error('Error clearing Apollo Client cache', { error });
      }
    }

    // 3. Clear redux-persist storage
    if (clearPersistentState) {
      try {
        await persistor?.purge();
      } catch (error) {
        console.error('Error purging persistent state', { error });
      }
    }

    // 4. Clear specific localStorage items
    if (clearLocalStorage) {
      const itemsToRemove = [
        'pendingDesignSelection',
        'pendingDesignAfterLogin',
        'redirectAfterLogin',
        'guest-invitation-data',
        'invitation-project-cache',
        'user-session-data',
        'apollo-cache-persist',
        'font-cache',
        'design-cache',
        'envelope-cache',
        'redux-persist-cache',
        // Session management items
        'session_start_time',
        'last_activity_time',
        'session_warning_shown',
        // Any other app-specific items
        'current_project_id',
        'selected_design_id',
        'guest_list_cache'
      ];

      if (!keepUserPreferences) {
        itemsToRemove.push(
          'user-preferences',
          'ui-settings',
          'favorite-designs',
          'recent-designs'
        );
      }

      itemsToRemove.forEach(item => {
        try {
          localStorage.removeItem(item);
        } catch (error) {
          console.warn('Could not remove localStorage item', { item, error });
        }
      });
    }

    // 5. Clear sessionStorage
    if (clearSessionStorage) {
      try {
        if (!keepUserPreferences) {
          sessionStorage.clear();
        } else {
          // Remove specific items while keeping user preferences
          const sessionItemsToRemove = [
            'current-session',
            'navigation-state',
            'form-data',
            'temp-project-data'
          ];
          sessionItemsToRemove.forEach(item => {
            sessionStorage.removeItem(item);
          });
        }
      } catch (error) {
        console.error('Error clearing sessionStorage', { error });
      }
    }

    // 6. Clear all browser storage (nuclear option)
    if (clearBrowserStorage) {
      try {
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear IndexedDB if available
        if ('indexedDB' in window) {
          // Note: This is a more advanced operation that might require specific handling
        }
      } catch (error) {
        console.error('Error clearing all browser storage', { error });
      }
    }

    // 7. Force garbage collection if available (Chrome DevTools)
    if (typeof window !== 'undefined' && (window as any).gc) {
      try {
        (window as any).gc();
      } catch (error) {
        // Ignore errors - gc() is not always available
      }
    }

  } catch (error) {
    console.error('Error during comprehensive reset', { error });
    throw error;
  }
};

/**
 * Legacy stateReset function - now uses comprehensiveReset
 * @deprecated Use comprehensiveReset instead for more control
 */
export const stateReset = async (): Promise<void> => {
  await comprehensiveReset({
    clearReduxState: true,
    clearPersistentState: true,
    clearApolloCache: true,
    clearLocalStorage: true,
    clearSessionStorage: false, // Don't clear session storage in legacy mode
    clearBrowserStorage: false,
    keepUserPreferences: false
  });
};

/**
 * Best-effort cleanup of unsaved draft projects before logout.
 * Reads current Redux state to find the active project, checks if it was
 * never saved (needs_initial_save), and deletes it via GraphQL.
 */
const deleteUnsavedDraftOnLogout = async (): Promise<void> => {
  try {
    const state = store.getState();
    const projectId = state.invitation?.invitationProjectState?.projectId;
    const userId = state.user?.userData?.userId;
    const token = state.user?.token;
    const lastSavedData = state.invitation?.lastSavedData;

    if (!projectId || !userId || !token || lastSavedData) return;

    const client = createApolloClient(token);
    if (!client) return;

    await client.mutate({
      mutation: gql`
        mutation DeleteUnsavedDraft($projectId: Int!, $requestingUserId: Int!) {
          deleteExistingInvitationProject(
            input: { projectId: $projectId, requestingUserId: $requestingUserId }
          ) { success }
        }
      `,
      variables: { projectId, requestingUserId: userId },
    });
  } catch (e) {
    // Fire-and-forget — don't block logout
  }
};

/**
 * Main logout function with enhanced reset capabilities
 * @param reason Optional reason for logout (for debugging)
 * @param resetOptions Optional reset configuration
 */
export const logout = async (
  reason: string = 'manual logout',
  resetOptions: ResetOptions = {}
): Promise<void> => {
  const redirectTo = resetOptions.redirectTo ?? '/log-ind/';
  const { redirectTo: _unused, ...resetOpts } = resetOptions;

  try {
    // Delete any unsaved draft project before clearing state
    await deleteUnsavedDraftOnLogout();

    // Perform comprehensive reset with default options for logout
    await comprehensiveReset({
      clearReduxState: true,
      clearPersistentState: true,
      clearApolloCache: true,
      clearLocalStorage: true,
      clearSessionStorage: true,
      clearBrowserStorage: false,
      keepUserPreferences: false,
      ...resetOpts // Allow override of default options (exclude redirectTo)
    });

    // Navigate after logging out
    navigate(redirectTo);
  } catch (error) {
    console.error('Error during logout', { reason, error });

    // Even if reset fails, still try to navigate away and clear basic auth
    try {
      store.dispatch(setUserAuthenticated(false));
      store.dispatch(setToken(null));
      navigate(redirectTo);
    } catch (navError) {
      console.error('Error during fallback logout', { navError });
    }

    throw error;
  }
};

/**
 * Session timeout specific logout - ensures complete state clearing
 */
export const sessionTimeoutLogout = async (timeoutReason: string): Promise<void> => {
  await logout(`session timeout: ${timeoutReason}`, {
    clearReduxState: true,
    clearPersistentState: true,
    clearApolloCache: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearBrowserStorage: false,
    keepUserPreferences: false
  });
};

/**
 * Emergency reset function for critical situations
 */
export const emergencyReset = async (): Promise<void> => {
  console.warn('Performing emergency reset - clearing everything!');
  
  await comprehensiveReset({
    clearReduxState: true,
    clearPersistentState: true,
    clearApolloCache: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearBrowserStorage: true, // Nuclear option
    keepUserPreferences: false
  });
  
  // Force reload the page after emergency reset
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};
/**
 * Debug function to test different reset scenarios
 * Available in browser console as window.testReset()
 */
export const testReset = async (scenario: 'manual' | 'session-timeout' | 'emergency' | 'comprehensive' = 'comprehensive'): Promise<void> => {
  switch (scenario) {
    case 'manual':
      await logout('test manual logout');
      break;
    case 'session-timeout':
      await sessionTimeoutLogout('test session timeout');
      break;
    case 'emergency':
      await emergencyReset();
      break;
    case 'comprehensive':
    default:
      await comprehensiveReset({
        clearReduxState: true,
        clearPersistentState: true,
        clearApolloCache: true,
        clearLocalStorage: true,
        clearSessionStorage: true,
        clearBrowserStorage: false,
        keepUserPreferences: false
      });
      break;
  }
};

// Make testReset available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).testReset = testReset;
  (window as any).emergencyReset = emergencyReset;
  (window as any).comprehensiveReset = comprehensiveReset;
}

